import { useMutation } from '@apollo/client';
import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Button, Tooltip } from '@material-ui/core';

import {
  AddFundScenarios,
  DeleteFundScenarios,
  ChangeFundScenarios,
  CalculateFundScenarios,
} from 'modules/graphql';

import { EeLoadingArea } from 'pages/Scenarios/components/EeLoadingArea';
import { EeTextValue } from 'pages/Scenarios/components/EeTextValue';
import { EePopover } from 'pages/Scenarios/components/EePopover';
import { ScenarioSettings } from './scenarioSettings';

import './style.scss';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks/useStores';
import { useSnackbar } from 'hooks/useSnackbar';

export const ScenariosPanel = observer((props: any) => {
  const { scenarios, fundCurrencySign } = props;

  const { fundScenarioStore } = useStores();

  const [settingsIsOpen, setOpenSettings] = useState(false);

  const { addSnackbar } = useSnackbar();

  const [addFundScenarios, { error: addFundScenarioError }] = useMutation(
    AddFundScenarios,
  );
  const [deleteFundScenarios, { error: deleteFundScenarioError }] = useMutation(
    DeleteFundScenarios,
  );
  const [changeFundScenarios, { error: changeFundScenarioError }] = useMutation(
    ChangeFundScenarios,
  );
  const [calculateFundScenarios, { error: calculationError }] = useMutation(
    CalculateFundScenarios,
  );

  let settingsIconRef = useRef();

  useEffect(() => {
    if (
      calculationError ||
      changeFundScenarioError ||
      deleteFundScenarioError ||
      addFundScenarioError
    ) {
      fundScenarioStore.isCalculated = true;
      fundScenarioStore.isLoading = false;

      addSnackbar('Failed to change fund scenario');

      console.error(
        'Failed to change fund scenario: ',
        calculationError,
        changeFundScenarioError,
        deleteFundScenarioError,
        addFundScenarioError,
      );
    }
  }, [
    calculationError,
    changeFundScenarioError,
    deleteFundScenarioError,
    addFundScenarioError,
  ]);

  const closeModal = () => {
    setOpenSettings(false);
  };

  async function updateScenarios(scenarioRows = [], deleted = []) {
    const mergeData = {
      toDelete: deleted,
      toAdd: scenarioRows.filter((sr: any) => !sr.id),
      toUpdate: scenarioRows.filter((sRow: any) =>
        scenarios.some((s: any) => s.id === sRow.id),
      ),
    };

    fundScenarioStore.isCalculated = false;

    if (mergeData.toDelete && mergeData.toDelete.length > 0) {
      //delete
      await deleteFundScenarios({
        variables: {
          scenarios: mergeData.toDelete.map((i: any) => i.id),
        },
      });
    }

    if (mergeData.toAdd && mergeData.toAdd.length > 0) {
      //add
      await addFundScenarios({
        variables: {
          scenarioSets: [
            {
              fundId: props.fundId,
              scenarios: mergeData.toAdd.map((s: any) => {
                return {
                  name: s.name,
                  position: s.position,
                  defaultExitDate: s.defaultExitDate,
                  defaultDilution: s.defaultDilution
                    ? parseFloat(s.defaultDilution)
                    : 0,
                };
              }),
            },
          ],
        },
      });
    }

    if (mergeData.toUpdate && mergeData.toUpdate.length > 0) {
      //update
      await changeFundScenarios({
        variables: {
          scenarios: mergeData.toUpdate.map((s: any) => {
            return {
              id: s.id,
              name: s.name,
              position: s.position,
              defaultExitDate: s.defaultExitDate,
              defaultDilution: s.defaultDilution
                ? parseFloat(s.defaultDilution)
                : 0,
            };
          }),
        },
      });
    }

    await calculateFundScenarios({
      variables: {
        fundId: props.fundId,
      },
    });

    // Close open rows
    props.onScenarioChange(Math.random());
  }

  return (
    <div className="d-flex flex-column position-relative bg-white border-bottom shadow-sm p-4 w-100 lens-section">
      {settingsIsOpen && (
        <ScenarioSettings
          scenarios={scenarios}
          open={settingsIsOpen}
          onClose={closeModal}
          onSubmit={updateScenarios}
          fundId={props.fundId}
          onUpdate={props.refetch}
        />
      )}
      <div className="d-flex flex-row justify-content-between">
        <div className="d-flex flex-row align-items-center">
          <h3 className="mb-0 mr-2">Scenarios</h3>
          {/*@ts-ignore*/}
          <IconButton
            size="small"
            disabled={!fundScenarioStore.isCalculated}
            onClick={() => setOpenSettings(true)}
            ref={settingsIconRef}
          >
            <i className="material-icons material-icons-outlined">settings</i>
          </IconButton>
          <EePopover
            open={false}
            anchorEl={settingsIconRef}
            onClose={() => {}}
            title="Check the companies!"
          >
            so that you see the result in Fund Level. When you add value for
            each company
          </EePopover>
        </div>
        <div>
          <Button
            color="primary"
            size="small"
            variant="contained"
            style={{
              width: 96,
              height: 40,
              fontSize: 16,
              textTransform: 'initial',
            }}
            onClick={() => {
              // @ts-ignore
              window.location += 'report/'
            }}
          >
            Export
          </Button>
        </div>
      </div>
      <div className="d-flex flex-row h-100">
        {scenarios.length === 0 ? (
          <div className="w-100 p-2 bg-light d-flex align-items-center justify-content-center">
            <EeLoadingArea position="relative" />
          </div>
        ) : (
          scenarios.map((scenario: any) => {
            return (
              <div
                key={scenario.id}
                className="scenario-column p-2"
                style={{ width: 248, marginRight: 44 }}
              >
                <h6 className="text-uppercase">{scenario.name}</h6>
                <div
                  className="underline mb-3 mt-1 lens-underline"
                  style={{ color: 'var(--color-gold)' }}
                />

                <div className="d-flex flex-row position-relative">
                  {!fundScenarioStore.isCalculated && (
                    <EeLoadingArea position="absolute" />
                  )}
                  <div className="d-flex flex-column pr-3">
                    <EeTextValue
                      title="book value"
                      value={scenario.bookValue}
                      formatProps={{
                        thousandSeparator: true,
                        prefix: fundCurrencySign,
                        decimalScale: 0,
                      }}
                    />
                    <EeTextValue
                      title="gross irr"
                      value={scenario.grossIrr * 100}
                      formatProps={{
                        suffix: '%',
                        decimalScale: 2,
                      }}
                    />
                  </div>
                  <div className="d-flex flex-column">
                    <EeTextValue
                      title="invested capital"
                      value={scenario.investedCapital}
                      formatProps={{
                        thousandSeparator: true,
                        prefix: fundCurrencySign,
                        decimalScale: 0,
                      }}
                    />
                    <EeTextValue
                      title="multiple"
                      value={scenario.grossMultiple}
                      formatProps={{
                        suffix: 'x',
                        decimalScale: 2,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});
